import React from 'react';
import { Link } from 'react-router-dom';
import { Book, Edit3, Music, Smile } from 'lucide-react';

const Programs = () => {
  const programs = [
    {
      title: "Play Group",
      age: "1.5 - 2.5 Years",
      desc: "A fun-filled introduction to social interaction and structured play. Focus is on sensory motor skills and language development.",
      icon: <Smile size={40} className="text-white" />,
      color: "var(--color-primary)",
      items: ["Sensory Play", "Story Time", "Music & Movement"]
    },
    {
      title: "Nursery",
      age: "2.5 - 3.5 Years",
      desc: "Building foundations for reading, writing, and math through interactive activities. Encouraging curiosity and exploration.",
      icon: <Book size={40} className="text-white" />,
      color: "var(--color-accent-dark)", // Pink
      items: ["Phonics", "Number Concepts", "Art & Craft"]
    },
    {
      title: "Junior KG",
      age: "3.5 - 4.5 Years",
      desc: "Enhancing communication skills and cognitive development. Preparing children for logical thinking and problem-solving.",
      icon: <Music size={40} className="text-white" />,
      color: "var(--color-secondary-dark)", // Amber
      items: ["Complex Sentences", "Pattern Recognition", "Public Speaking"]
    },
    {
      title: "Senior KG",
      age: "4.5 - 5.5 Years",
      desc: "Preparing for formal schooling with advanced literacy and numeracy skills. Focus on independence and confidence.",
      icon: <Edit3 size={40} className="text-white" />,
      color: "var(--color-success)", // Green
      items: ["Reading Fluency", "Basic Math Operations", "Creative Writing"]
    }
  ];

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <div className="container text-center">
          <h1 className="page-title">Our Programs</h1>
          <p className="page-subtitle">Age-appropriate curriculum designed for every stage of early childhood.</p>
        </div>
      </div>

      <div className="container section">
        <div className="programs-grid">
          {programs.map((prog, index) => (
            <div key={index} className="program-card">
              <div className="program-header" style={{ backgroundColor: prog.color }}>
                <div className="program-icon-wrapper">
                  {prog.icon}
                </div>
                <h3 className="program-title text-white">{prog.title}</h3>
                <span className="program-age">{prog.age}</span>
              </div>
              <div className="program-body">
                <p className="program-desc">{prog.desc}</p>
                <div className="program-separator"></div>
                <ul className="program-features">
                  {prog.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
                <Link to="/contact" className="btn btn-outline w-full" style={{ color: prog.color, borderColor: prog.color }}>Details</Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .page-header {
          background-color: var(--color-primary-dark); /* Different shade for variety */
        }

        .programs-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 2rem;
          max-width: 1000px;
          margin: 0 auto;
        }

        .program-card {
          background: white;
          border-radius: 1.5rem;
          overflow: hidden;
          box-shadow: var(--shadow-md);
          transition: transform 0.3s;
        }

        .program-card:hover {
          transform: translateY(-8px);
          box-shadow: var(--shadow-xl);
        }

        .program-header {
          padding: 2.5rem 2rem;
          text-align: center;
          position: relative;
        }

        .program-icon-wrapper {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
        }

        .program-title {
          font-size: 1.75rem;
          font-weight: 800;
          margin-bottom: 0.5rem;
        }

        .program-age {
          display: inline-block;
          background: rgba(255, 255, 255, 0.9);
          padding: 0.25rem 1rem;
          border-radius: 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--color-text-main);
        }

        .program-body {
          padding: 2rem;
        }

        .program-desc {
          color: var(--color-text-light);
          font-size: 1rem;
          margin-bottom: 1.5rem;
          text-align: center;
        }

        .program-separator {
          height: 1px;
          background: #e2e8f0;
          margin: 1.5rem 0;
        }

        .program-features {
          list-style: none;
          margin-bottom: 1.5rem;
        }

        .program-features li {
          margin-bottom: 0.5rem;
          padding-left: 1.5rem;
          position: relative;
          color: var(--color-text-main);
          font-weight: 500;
        }

        .program-features li::before {
          content: '•';
          position: absolute;
          left: 0;
          color: var(--color-text-light);
          font-size: 1.5rem;
          line-height: 1rem;
        }

        @media (max-width: 768px) {
          .programs-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Programs;